import React, { useState, useEffect } from 'react';
import { auditAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Filter, RefreshCw, Eye, ChevronDown, ChevronRight, AlertCircle, Settings, ToggleLeft, ToggleRight } from 'lucide-react';

const ACTION_COLORS = {
  create: 'bg-green-100 text-green-700 border-green-200',
  update: 'bg-blue-100 text-blue-700 border-blue-200',
  delete: 'bg-red-100 text-red-700 border-red-200',
};

const ENTITY_TYPES = [
  { value: '', label: 'All entities' },
  { value: 'fund', label: 'Fund' },
  { value: 'investment', label: 'Investment' },
  { value: 'investor_account', label: 'Investor Account' },
  { value: 'investor_contact', label: 'Investor Contact' },
  { value: 'drawdown', label: 'Drawdown' },
  { value: 'bank_demat_record', label: 'Bank/Demat Record' },
  { value: 'audit_settings', label: 'Audit Settings' },
];

const fmtTs = (ts) => {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return ts; }
};

const fmtVal = (v) => {
  if (v === null || v === undefined) return <span className="text-gray-400 italic">—</span>;
  if (typeof v === 'object') return <code className="text-[10px] bg-slate-50 px-1 rounded">{JSON.stringify(v).slice(0, 80)}</code>;
  if (typeof v === 'boolean') return <span className={v ? 'text-green-600' : 'text-gray-500'}>{String(v)}</span>;
  const s = String(v);
  return s.length > 80 ? s.slice(0, 80) + '…' : s;
};

const DiffPanel = ({ entry }) => {
  if (entry.action === 'create' && entry.snapshot) {
    return (
      <div className="text-xs space-y-1 p-3 bg-green-50/50 rounded">
        <p className="font-medium text-green-800 mb-1">Created with values:</p>
        <pre className="text-[10px] text-gray-700 whitespace-pre-wrap break-all">{JSON.stringify(entry.snapshot, null, 2).slice(0, 4000)}</pre>
      </div>
    );
  }
  if (entry.action === 'delete' && entry.snapshot) {
    return (
      <div className="text-xs space-y-1 p-3 bg-red-50/50 rounded">
        <p className="font-medium text-red-800 mb-1">Deleted record (snapshot):</p>
        <pre className="text-[10px] text-gray-700 whitespace-pre-wrap break-all">{JSON.stringify(entry.snapshot, null, 2).slice(0, 4000)}</pre>
      </div>
    );
  }
  if (entry.action === 'update' && entry.diff && Object.keys(entry.diff).length > 0) {
    const rows = Object.entries(entry.diff);
    return (
      <div className="text-xs p-3 bg-blue-50/40 rounded">
        <p className="font-medium text-blue-800 mb-2">{rows.length} field{rows.length !== 1 ? 's' : ''} changed:</p>
        <table className="w-full">
          <thead>
            <tr className="text-gray-500 text-[10px] uppercase">
              <th className="text-left font-medium pr-3 w-32">Field</th>
              <th className="text-left font-medium pr-3">Old</th>
              <th className="text-left font-medium">New</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([key, val]) => (
              <tr key={key} className="align-top border-t border-blue-100">
                <td className="pr-3 py-1 text-gray-700 font-mono">{key}</td>
                <td className="pr-3 py-1 line-through text-red-600 break-all">{fmtVal(val.old)}</td>
                <td className="py-1 text-green-700 break-all">{fmtVal(val.new)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return <p className="text-xs text-gray-400 italic p-3">No detailed diff available.</p>;
};

const SettingsPanel = ({ settings, onChange, isAdmin }) => {
  if (!isAdmin) return null;
  const [retention, setRetention] = useState(settings?.retention_days ?? 365);
  useEffect(() => { setRetention(settings?.retention_days ?? 365); }, [settings]);

  const toggle = async () => {
    await auditAPI.updateSettings({ enabled: !settings.enabled });
    onChange();
  };
  const saveRetention = async () => {
    if (retention < 30 || retention > 3650) {
      alert('Retention must be between 30 and 3650 days');
      return;
    }
    await auditAPI.updateSettings({ retention_days: retention });
    onChange();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4" data-testid="audit-settings-panel">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-md ${settings?.enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
            <Shield size={18} />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">Audit Trail</p>
            <p className="text-xs text-gray-500">Capture every create / update / delete across the portal</p>
          </div>
        </div>
        <button
          onClick={toggle}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
            settings?.enabled ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="audit-toggle"
        >
          {settings?.enabled ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
          {settings?.enabled ? 'Enabled' : 'Disabled'}
        </button>
      </div>
      <div className="flex items-center gap-3 mt-3 pt-3 border-t">
        <label className="text-xs font-medium text-gray-600">Retention (days)</label>
        <input
          type="number"
          min={30}
          max={3650}
          value={retention}
          onChange={e => setRetention(parseInt(e.target.value || '0'))}
          className="border rounded px-2 py-1 text-sm w-24"
          data-testid="audit-retention-input"
        />
        <button onClick={saveRetention} className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700" data-testid="audit-retention-save">Save</button>
        <p className="text-[11px] text-gray-400 ml-auto">Logs older than this are auto-purged.</p>
      </div>
    </div>
  );
};

export default function AuditTrail() {
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [filters, setFilters] = useState({ entity_type: '', action: '', start_date: '', end_date: '' });
  const [expanded, setExpanded] = useState({});

  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  useEffect(() => { loadSettings(); }, []);
  useEffect(() => { if (settings?.enabled) loadLogs(); else setLogs([]); /* if disabled, no logs incoming */ }, [settings, page, JSON.stringify(filters)]);

  const loadSettings = async () => {
    try {
      const r = await auditAPI.getSettings();
      setSettings(r.data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await auditAPI.list(params);
      setLogs(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      console.error(e);
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle size={18} className="text-amber-600" />
          <p className="text-sm text-amber-800">Audit Trail is restricted to Admin role only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="audit-trail-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Shield size={22} className="text-blue-600" /> Audit Trail
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Track every create, update, and delete across the portal.</p>
        </div>
        <button onClick={() => { loadSettings(); loadLogs(); }} className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded text-sm hover:bg-gray-50" data-testid="audit-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <SettingsPanel settings={settings} onChange={loadSettings} isAdmin={isAdmin} />

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 mb-3 flex flex-wrap items-center gap-2" data-testid="audit-filters">
        <Filter size={14} className="text-gray-400" />
        <select value={filters.entity_type} onChange={e => { setFilters({ ...filters, entity_type: e.target.value }); setPage(1); }} className="border rounded px-2 py-1 text-sm" data-testid="audit-filter-entity">
          {ENTITY_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={filters.action} onChange={e => { setFilters({ ...filters, action: e.target.value }); setPage(1); }} className="border rounded px-2 py-1 text-sm" data-testid="audit-filter-action">
          <option value="">All actions</option>
          <option value="create">Create</option>
          <option value="update">Update</option>
          <option value="delete">Delete</option>
        </select>
        <input type="date" value={filters.start_date} onChange={e => { setFilters({ ...filters, start_date: e.target.value }); setPage(1); }} className="border rounded px-2 py-1 text-sm" placeholder="From" data-testid="audit-filter-start" />
        <span className="text-gray-300">→</span>
        <input type="date" value={filters.end_date} onChange={e => { setFilters({ ...filters, end_date: e.target.value }); setPage(1); }} className="border rounded px-2 py-1 text-sm" placeholder="To" data-testid="audit-filter-end" />
        {(filters.entity_type || filters.action || filters.start_date || filters.end_date) && (
          <button onClick={() => { setFilters({ entity_type: '', action: '', start_date: '', end_date: '' }); setPage(1); }} className="text-xs text-gray-500 underline ml-2" data-testid="audit-filter-clear">Clear all</button>
        )}
        <span className="ml-auto text-xs text-gray-400">{total.toLocaleString()} entries total</span>
      </div>

      {/* Logs table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {!settings?.enabled ? (
          <div className="p-12 text-center text-gray-500">
            <Shield size={32} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm">Audit trail is currently disabled.</p>
            <p className="text-xs text-gray-400 mt-1">Toggle it on above to start capturing changes.</p>
          </div>
        ) : loading ? (
          <div className="p-12 text-center text-gray-400">Loading logs…</div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center text-gray-400 text-sm">No audit entries match your filters.</div>
        ) : (
          <table className="min-w-full text-sm" data-testid="audit-logs-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-gray-500 border-b">
              <tr>
                <th className="px-2 py-2 w-6"></th>
                <th className="px-3 py-2 text-left">Timestamp</th>
                <th className="px-3 py-2 text-left">User</th>
                <th className="px-3 py-2 text-left">Action</th>
                <th className="px-3 py-2 text-left">Entity</th>
                <th className="px-3 py-2 text-left">Label</th>
                <th className="px-3 py-2 text-left">Changes</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(l => {
                const isExp = expanded[l.id];
                const changeSummary =
                  l.action === 'create' ? 'New record' :
                  l.action === 'delete' ? 'Record removed' :
                  l.diff ? `${Object.keys(l.diff).length} field${Object.keys(l.diff).length !== 1 ? 's' : ''}` : '—';
                return (
                  <React.Fragment key={l.id}>
                    <tr className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded(e => ({ ...e, [l.id]: !e[l.id] }))} data-testid={`audit-row-${l.id}`}>
                      <td className="px-2 py-2 text-gray-400">{isExp ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                      <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap">{fmtTs(l.timestamp)}</td>
                      <td className="px-3 py-2 text-xs">
                        <div className="font-medium text-gray-800">{l.user_name || l.user_email}</div>
                        <div className="text-[10px] text-gray-400">{l.user_role}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${ACTION_COLORS[l.action] || 'bg-gray-100'}`}>{l.action}</span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{l.entity_type}</td>
                      <td className="px-3 py-2 text-xs font-medium text-gray-800 truncate max-w-[260px]">{l.entity_label || l.entity_id?.slice(0, 8)}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{changeSummary}</td>
                    </tr>
                    {isExp && (
                      <tr className="border-b bg-slate-50/40">
                        <td></td>
                        <td colSpan={6} className="px-3 py-2"><DiffPanel entry={l} /></td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {settings?.enabled && total > pageSize && (
        <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
          <span>Page {page} of {Math.ceil(total / pageSize)}</span>
          <div className="flex gap-1">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-50" data-testid="audit-prev-page">Prev</button>
            <button disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-50" data-testid="audit-next-page">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Reusable per-entity History tab (used by Fund / Investment / Investor Account detail pages)
export const EntityAuditHistory = ({ entityType, entityId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        // Check audit settings first
        const s = await auditAPI.getSettings();
        if (!alive) return;
        setEnabled(s.data?.enabled);
        if (!s.data?.enabled) { setLogs([]); return; }
        const r = await auditAPI.getEntityHistory(entityType, entityId);
        if (alive) setLogs(r.data.items || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [entityType, entityId]);

  if (loading) return <div className="text-sm text-gray-400 py-6 text-center">Loading history…</div>;
  if (!enabled) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800 flex items-center gap-2">
        <AlertCircle size={14} /> Audit Trail is currently disabled. Enable it from the Audit Trail page to start capturing changes.
      </div>
    );
  }
  if (!logs.length) return <div className="text-sm text-gray-400 py-6 text-center">No history captured yet for this record.</div>;

  return (
    <div className="space-y-2" data-testid={`entity-audit-history-${entityType}`}>
      {logs.map(l => {
        const isExp = expanded[l.id];
        return (
          <div key={l.id} className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <button onClick={() => setExpanded(e => ({ ...e, [l.id]: !e[l.id] }))} className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-slate-50">
              <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${ACTION_COLORS[l.action] || 'bg-gray-100'}`}>{l.action}</span>
              <span className="text-xs text-gray-700 flex-1">
                <span className="font-medium">{l.user_name || l.user_email}</span>
                <span className="text-gray-400"> · {fmtTs(l.timestamp)}</span>
              </span>
              {l.action === 'update' && l.diff && (
                <span className="text-[10px] text-gray-500">{Object.keys(l.diff).length} field{Object.keys(l.diff).length !== 1 ? 's' : ''}</span>
              )}
              {isExp ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
            </button>
            {isExp && <div className="border-t"><DiffPanel entry={l} /></div>}
          </div>
        );
      })}
    </div>
  );
};
